import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, take } from 'rxjs/operators';
import { DocumentTypeCategory } from 'src/app/compliance/models/document-type-category.model';
import { KeyValue } from 'src/app/shared/models/key-value.model';
import { ToasterService } from 'src/app/shared/services/toaster.service';
import { isEmpty } from 'src/app/shared/utilities/obj-utilities';
import { DocAdminNSwagMigrationService } from '../../../compliance/api/migration/doc-admin-nswag-migration.service';
import { DocumentTypes } from '../../../compliance/models/document-types.model';
import { DocumentTypeActions } from '../actions';
import { selectPartnerNetworkTaskTypes } from '../doc-admin.selector';
import { DocumentAdminClient } from 'src/app/compliance/api/document-admin-client.service';
import { CategoryLineOfBusiness } from 'src/app/compliance/enums/category-line-of-business.enum';
import { FacilityTypeDto } from 'src/app/compliance/api/models/facility-type-dto.model';
import { WorkTypeSettingsDto } from 'src/app/compliance/api/models/work-type-settings-dto.model';
import { LocumsContractTypeSettingsDto } from 'src/app/compliance/api/models/locums-contract-type-settings-dto.model';
import { LineOfBusinessesClient } from 'src/app/compliance/api/line-of-businesses-client.service';
import { ManualReviewCandidateTypeDto } from 'src/app/compliance/api/models/manual-review-candidate-type-dto.model';
@Injectable()
export class DocumentTypeDetailsEffects {
    constructor(
        private readonly actions$: Actions,
        private readonly docAdminService: DocAdminNSwagMigrationService,
        private readonly docAdminClient: DocumentAdminClient,
        private readonly lineOfBusinessesClient: LineOfBusinessesClient,
        private readonly toasterService: ToasterService,
        private readonly store: Store<{}>
    ) {}

    loadPermissions$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypesDetail.getPermissions),
            switchMap((action) =>
                this.docAdminService.getPermissions().pipe(
                    map((permissions) =>
                        DocumentTypeActions.DocumentTypesDetail.getPermissionsSuccess({ permissions })
                    ),
                    catchError((error) => of(DocumentTypeActions.DocumentTypesDetail.getPermissionsFailure({ error })))
                )
            )
        )
    );

    loadInitFacilityLookup$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypesDetail.getInitFacilityLookup),
            switchMap((action) =>
                this.docAdminService.getFacilityLookups(action.id.toString(), action.lob).pipe(
                    map((facilities: KeyValue<number, string>[]) =>
                        DocumentTypeActions.DocumentTypesDetail.getInitFacilityLookupSuccess({
                            facility: facilities?.find((f) => f.id === action.id)
                        })
                    ),
                    catchError((error) =>
                        of(DocumentTypeActions.DocumentTypesDetail.getInitFacilityLookupFailure({ error }))
                    )
                )
            )
        )
    );

    loadFacilityLookups$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypesDetail.getFacilityLookups),
            switchMap((action) =>
                this.docAdminService.getFacilityLookups(action.name, action.lob).pipe(
                    map((facilities: KeyValue<number, string>[]) =>
                        DocumentTypeActions.DocumentTypesDetail.getFacilityLookupsSuccess({ facilities })
                    ),
                    catchError((error) =>
                        of(DocumentTypeActions.DocumentTypesDetail.getFacilityLookupsFailure({ error }))
                    )
                )
            )
        )
    );

    loadInitAssociationLookup$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypesDetail.getInitAssociationLookup),
            switchMap((action) =>
                this.docAdminService.getAssociationLookups(action.id.toString()).pipe(
                    map((associations: KeyValue<number, string>[]) =>
                        DocumentTypeActions.DocumentTypesDetail.getInitAssociationLookupSoccess({
                            association: associations?.find((a) => a.id === action.id)
                        })
                    ),
                    catchError((error) =>
                        of(DocumentTypeActions.DocumentTypesDetail.getInitFacilityLookupFailure({ error }))
                    )
                )
            )
        )
    );

    loadAssociationLookups$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypesDetail.getAssociationLookups),
            switchMap((action) =>
                this.docAdminService.getAssociationLookups(action.name).pipe(
                    map((associations: KeyValue<number, string>[]) =>
                        DocumentTypeActions.DocumentTypesDetail.getAssociationLookupsSuccess({ associations })
                    ),
                    catchError((error) =>
                        of(DocumentTypeActions.DocumentTypesDetail.getAssociationLookupsFailure({ error }))
                    )
                )
            )
        )
    );

    loadCandidateTaskTypes$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypesDetail.getCandidateTaskTypes),
            switchMap(() =>
                this.docAdminService.getCandidateTaskTypes().pipe(
                    map((candidateTaskTypes: KeyValue<number, string>[]) =>
                        DocumentTypeActions.DocumentTypesDetail.getCandidateTaskTypesSuccess({ candidateTaskTypes })
                    ),
                    catchError((error) =>
                        of(DocumentTypeActions.DocumentTypesDetail.getCandidateTaskTypesFailure({ error }))
                    )
                )
            )
        )
    );

    loadPartnerNetworkTaskTypes$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypesDetail.getPartnerNetworkTaskTypes),
            switchMap(() => this.store.select(selectPartnerNetworkTaskTypes).pipe(take(1))),
            filter((pntt) => (pntt ?? []).length <= 0),
            switchMap(() =>
                this.docAdminService.getPartnerNetworkTaskTypes().pipe(
                    map((partnerNetworkTaskTypes: KeyValue<number, string>[]) =>
                        DocumentTypeActions.DocumentTypesDetail.getPartnerNetworkTaskTypesSuccess({
                            partnerNetworkTaskTypes
                        })
                    ),
                    catchError((error) =>
                        of(DocumentTypeActions.DocumentTypesDetail.getPartnerNetworkTaskTypesFailure({ error }))
                    )
                )
            )
        )
    );

    loadSpecialties$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypesDetail.getSpecialtyLookups),
            switchMap((action) =>
                this.docAdminService.getSpecialties(action.getLocums).pipe(
                    map((specialties: KeyValue<number, string>[]) => {
                        specialties.unshift({ key: null, name: 'All Specialties' });
                        return DocumentTypeActions.DocumentTypesDetail.getSpecialtyLookupsSuccess({ specialties });
                    }),
                    catchError((error) =>
                        of(DocumentTypeActions.DocumentTypesDetail.getSpecialtyLookupsFailure({ error }))
                    )
                )
            )
        )
    );

    loadRequirementLevels$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypesDetail.getRequirementLevelLookups),
            switchMap((action) =>
                this.docAdminService.getRequirementLevels().pipe(
                    map((requirementLevels: KeyValue<number, string>[]) => {
                        return DocumentTypeActions.DocumentTypesDetail.getRequirementLevelLookupsSuccess({
                            requirementLevels
                        });
                    }),
                    catchError((error) =>
                        of(DocumentTypeActions.DocumentTypesDetail.getRequirementLevelLookupsFailure({ error }))
                    )
                )
            )
        )
    );

    loadStates$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypesDetail.getStateLookups),
            switchMap(() =>
                this.docAdminService.getStates().pipe(
                    map((states: KeyValue<number, string>[]) => {
                        states.unshift({ key: null, name: 'All States' });
                        return DocumentTypeActions.DocumentTypesDetail.getStateLookupsSuccess({ states });
                    }),
                    catchError((error) => of(DocumentTypeActions.DocumentTypesDetail.getStateLookupsFailure({ error })))
                )
            )
        )
    );

    loadFacilityTypes$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypesDetail.getFacilityTypeLookups),
            switchMap(() =>
                this.docAdminService.getFacilityTypes().pipe(
                    map((facilityTypes: FacilityTypeDto[]) => {
                        return DocumentTypeActions.DocumentTypesDetail.getFacilityTypeLookupsSuccess({ facilityTypes });
                    }),
                    catchError((error) =>
                        of(DocumentTypeActions.DocumentTypesDetail.getFacilityTypeLookupsFailure({ error }))
                    )
                )
            )
        )
    );

    loadManualReviewCandidateTypeSettings$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypesDetail.getManualReviewCandidateTypeSettingLookups),
            switchMap(() =>
                this.docAdminService.getManualReviewCandidateTypes().pipe(
                    map((manualReviewTypes: ManualReviewCandidateTypeDto[]) => {
                        return DocumentTypeActions.DocumentTypesDetail.getManualReviewCandidateTypeSettingLookupsSuccess(
                            {
                                manualReviewTypes
                            }
                        );
                    }),
                    catchError((error) =>
                        of(
                            DocumentTypeActions.DocumentTypesDetail.getManualReviewCandidateTypeSettingLookupsFailure({
                                error
                            })
                        )
                    )
                )
            )
        )
    );

    loadWorkTypeSettings$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypesDetail.getWorkTypeSettingsLookups),
            switchMap(() =>
                this.docAdminService.getWorkTypeSettings().pipe(
                    map((workTypeSettings: WorkTypeSettingsDto[]) => {
                        return DocumentTypeActions.DocumentTypesDetail.getWorkTypeSettingLookupsSuccess({
                            workTypeSettings
                        });
                    }),
                    catchError((error) =>
                        of(DocumentTypeActions.DocumentTypesDetail.getWorkTypeSettingLookupsFailure({ error }))
                    )
                )
            )
        )
    );

    loadLocumsContractTypeSettings$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypesDetail.getLocumsContractTypeSettingsLookups),
            switchMap(() =>
                this.docAdminService.getLocumsContractTypeSettings().pipe(
                    map((locumsContractTypeSettings: LocumsContractTypeSettingsDto[]) => {
                        return DocumentTypeActions.DocumentTypesDetail.getLocumsContractTypeSettingLookupsSuccess({
                            locumsContractTypeSettings
                        });
                    }),
                    catchError((error) =>
                        of(
                            DocumentTypeActions.DocumentTypesDetail.getLocumsContractTypeSettingLookupsFailure({
                                error
                            })
                        )
                    )
                )
            )
        )
    );

    loadLinesOfBusiness$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypesDetail.getLinesOfBusinessLookups),
            switchMap(() =>
                this.lineOfBusinessesClient.all().pipe(
                    map((a) => a.map((x) => ({ key: x.id, id: x.id, name: x.name }) as KeyValue<number, string>)),
                    map((linesOfBusiness: KeyValue<number, string>[]) => {
                        return DocumentTypeActions.DocumentTypesDetail.getLinesOfBusinessLookupsSuccess({
                            linesOfBusiness
                        });
                    }),
                    catchError((error) =>
                        of(DocumentTypeActions.DocumentTypesDetail.getLinesOfBusinessLookupsFailure({ error }))
                    )
                )
            )
        )
    );

    loadDocumentTypeCategories$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypesDetail.getDocumentTypeCategories),
            switchMap((action) => {
                return this.docAdminService.getDocumentTypesCategories(action.catLob).pipe(
                    map((documentTypeCatories: DocumentTypeCategory[]) =>
                        DocumentTypeActions.DocumentTypesDetail.getDocumentTypeCategoriesSuccess({
                            documentTypeCatories
                        })
                    ),
                    catchError((error) =>
                        of(DocumentTypeActions.DocumentTypesDetail.getDocumentTypeCategoriesFailure({ error }))
                    )
                );
            })
        )
    );

    loadDocumentTypeCategoriesList$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypesDetail.getDocumentTypeCategoriesList),
            switchMap((action) => {
                return this.docAdminService.getDocumentTypesCategories(CategoryLineOfBusiness.All).pipe(
                    map((documentTypeCategoriesList: string[]) =>
                        DocumentTypeActions.DocumentTypesDetail.getDocumentTypeCategoriesListSuccess({
                            documentTypeCategoriesList
                        })
                    ),
                    catchError((error) =>
                        of(DocumentTypeActions.DocumentTypesDetail.getDocumentTypeCategoriesListFailure({ error }))
                    )
                );
            })
        )
    );

    getOcrEnabledStatus$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypes.getOcrEnabledStatus),
            switchMap((action) => {
                return this.docAdminService.getEnableOcrStatus().pipe(
                    map((isOcrEnabled: boolean) =>
                        DocumentTypeActions.DocumentTypes.getOcrEnabledStatusSuccess({ isOcrEnabled })
                    ),
                    catchError((error) => of(DocumentTypeActions.DocumentTypes.getOcrEnabledStatusFailure({ error })))
                );
            })
        )
    );

    toggleOcrEnabled$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypes.toggleOcrEnabled),
            mergeMap((action) => {
                return this.docAdminService.toggleOcrStatus().pipe(
                    map(() =>
                        DocumentTypeActions.DocumentTypes.toggleOcrEnabledSuccess({ isOcrToggleSuccessful: true })
                    ),
                    catchError((error) => {
                        this.handleToast('Failure changing OCR Status');
                        return of(DocumentTypeActions.DocumentTypes.toggleOcrEnabledFailure({ error }));
                    })
                );
            })
        )
    );

    uploadDocument$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypesDetail.uploadDocument),
            mergeMap((action) =>
                this.docAdminService.uploadDocument(action.document).pipe(
                    map((document: DocumentTypes) => {
                        if (!isEmpty(action.documentFile.document)) {
                            this.handleToast('Success Uploading Document');
                            return DocumentTypeActions.DocumentTypesDetail.uploadDocumentFile({
                                documentFile: action.documentFile,
                                documentId: document.id
                            });
                        }
                        this.handleToast('Success Uploading Document');
                        return DocumentTypeActions.DocumentTypesDetail.uploadDocumentSuccess({ document });
                    }),
                    catchError((error) => {
                        this.handleToast('Failure Uploading Document');
                        return of(DocumentTypeActions.DocumentTypesDetail.uploadDocumentFailure({ error }));
                    })
                )
            )
        )
    );

    getDocument$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypesDetail.getDocument),
            switchMap((action) =>
                this.docAdminService.getFacilitySpecificDocument(action.documentId).pipe(
                    map((document: DocumentTypes) => {
                        this.handleToast('Success Getting Document');
                        return DocumentTypeActions.DocumentTypesDetail.getDocumentSuccess({ document });
                    }),
                    catchError((error) => {
                        this.handleToast('Failure Getting Document');
                        return of(DocumentTypeActions.DocumentTypesDetail.getDocumentFailure({ error }));
                    })
                )
            )
        )
    );

    initializeDocument$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypesDetail.initializeDocument),
            switchMap((action) =>
                this.docAdminService.initializeDocument().pipe(
                    map((document: DocumentTypes) =>
                        DocumentTypeActions.DocumentTypesDetail.initializeDocumentSuccess({ document })
                    ),
                    catchError((error) => {
                        this.handleToast('Failure Getting Document');
                        return of(DocumentTypeActions.DocumentTypesDetail.getDocumentFailure({ error }));
                    })
                )
            )
        )
    );

    updateDocument$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypesDetail.updateDocument),
            mergeMap((action) =>
                this.docAdminService.updateDocument(action.document).pipe(
                    map((document: DocumentTypes) => {
                        if (!isEmpty(action.documentFile.document)) {
                            this.handleToast('Success Updating Document');
                            return DocumentTypeActions.DocumentTypesDetail.uploadDocumentFile({
                                documentFile: action.documentFile,
                                documentId: document.id
                            });
                        }
                        this.handleToast('Success Updating Document');
                        return DocumentTypeActions.DocumentTypesDetail.updateDocumentSuccess({ document });
                    }),
                    catchError((error) => {
                        const message = error.status === 400 ? error.error : 'Failure Updating Document';
                        this.handleToast(message);
                        return of(DocumentTypeActions.DocumentTypesDetail.updateDocumentFailure({ error }));
                    })
                )
            )
        )
    );

    disableDocument$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypesDetail.disableDocument),
            mergeMap((action) =>
                this.docAdminService.disableDocument(action.document).pipe(
                    map((document: DocumentTypes) => {
                        if (!isEmpty(action.documentFile.document)) {
                            this.handleToast('Disabled requirement successfully.');
                            return DocumentTypeActions.DocumentTypesDetail.uploadDocumentFile({
                                documentFile: action.documentFile,
                                documentId: document.id
                            });
                        }
                        this.handleToast('Disabled requirement successfully.');

                        return DocumentTypeActions.DocumentTypesDetail.disableDocumentSuccess({ document });
                    }),
                    catchError((error) => {
                        const message = error.status === 400 ? error.error : 'Failure Disabling Document';
                        this.handleToast(message);
                        return of(DocumentTypeActions.DocumentTypesDetail.disableDocumentFailure({ error }));
                    })
                )
            )
        )
    );

    enableDocument$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypesDetail.enableDocument),
            mergeMap((action) =>
                this.docAdminService.enableDocument(action.document).pipe(
                    map((document: DocumentTypes) => {
                        if (!isEmpty(action.documentFile.document)) {
                            this.handleToast('Re-enabled requirement successfully.');
                            return DocumentTypeActions.DocumentTypesDetail.uploadDocumentFile({
                                documentFile: action.documentFile,
                                documentId: document.id
                            });
                        }
                        this.handleToast('Re-enabled requirement successfully.');
                        return DocumentTypeActions.DocumentTypesDetail.enableDocumentSuccess({ document });
                    }),
                    catchError((error) => {
                        const message = error.status === 400 ? error.error : 'Failure Enabling Document';
                        this.handleToast(message);
                        return of(DocumentTypeActions.DocumentTypesDetail.enableDocumentFailure({ error }));
                    })
                )
            )
        )
    );

    uploadDocumentFile$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypesDetail.uploadDocumentFile),
            switchMap((action) =>
                this.docAdminService.uploadDocumentFile(action.documentFile, action.documentId).pipe(
                    map((document: DocumentTypes) => {
                        return DocumentTypeActions.DocumentTypesDetail.uploadDocumentFileSuccess({ document });
                    }),
                    catchError((error) => {
                        this.handleToast('Failure Uploading Document');
                        return of(DocumentTypeActions.DocumentTypesDetail.uploadDocumentFileFailure({ error }));
                    })
                )
            )
        )
    );

    createNewDocument$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypesDetail.createNewDocument),
            switchMap((action) =>
                this.docAdminService.createNewDocument(action.documentId, action.pages).pipe(
                    map((document: DocumentTypes) => {
                        this.handleToast('Success Creating New Document');
                        return DocumentTypeActions.DocumentTypesDetail.createNewDocumentSuccess({ document });
                    }),
                    catchError((error) => {
                        this.handleToast('Error Creating New Document');
                        return of(DocumentTypeActions.DocumentTypesDetail.createNewDocumentFailure({ error }));
                    })
                )
            )
        )
    );

    removeRequirement$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypesDetail.removeRequirement),
            switchMap((action) =>
                this.docAdminService.removeRequirement(action.documentId).pipe(
                    map((document: DocumentTypes) => {
                        this.handleToast('Success Removing Requirement');
                        return DocumentTypeActions.DocumentTypesDetail.removeRequirementSuccess({ document });
                    }),
                    catchError((error) => {
                        this.handleToast('Error Removing Requirement');
                        return of(DocumentTypeActions.DocumentTypesDetail.removeRequirementFailure({ error }));
                    })
                )
            )
        )
    );

    loadRequirementPermissions$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypes.getRequirementPermissions),
            switchMap(() => {
                return this.docAdminClient.getDocAdminRequirementPermissions().pipe(
                    map((permissions) =>
                        DocumentTypeActions.DocumentTypes.getRequirementPermissionsSuccess({ permissions })
                    ),
                    catchError((error) =>
                        of(DocumentTypeActions.DocumentTypes.getRequirementPermissionsFailure({ error }))
                    )
                );
            })
        )
    );

    handleToast(message: string) {
        this.toasterService.toast(message);
    }
}
